// studio-export.js — admin-only high-res JPEG / PDF export of the current poster.
// Hidden from customers; only appears when the page URL has ?studio=1 (or #studio).
// No external libraries: builds a valid one-page PDF embedding a JPEG (DCTDecode).
(function () {
  const PT_PER_CM = 28.3465; // PDF points per centimetre

  function loadImg(src) {
    return new Promise((res, rej) => {
      const im = new Image();
      im.onload = () => res(im);
      im.onerror = rej;
      im.src = src;
    });
  }

  // PNG data URL → flattened JPEG (white background) + pixel dimensions
  async function pngToJpeg(pngDataUrl, quality) {
    const img = await loadImg(pngDataUrl);
    const c = document.createElement("canvas");
    c.width = img.naturalWidth; c.height = img.naturalHeight;
    const x = c.getContext("2d");
    x.fillStyle = "#ffffff"; x.fillRect(0, 0, c.width, c.height);
    x.drawImage(img, 0, 0);
    return { dataUrl: c.toDataURL("image/jpeg", quality || 0.95), w: c.width, h: c.height };
  }

  // If already a JPEG data URL, use it as-is (just read its pixel size) so we
  // don't redraw a huge image onto a second canvas (memory!); else convert.
  async function ensureJpeg(dataUrl, quality) {
    if (dataUrl.indexOf("data:image/jpeg") === 0) {
      const img = await loadImg(dataUrl);
      return { dataUrl, w: img.naturalWidth, h: img.naturalHeight };
    }
    return pngToJpeg(dataUrl, quality);
  }

  // Patch the JFIF APP0 segment so the JPEG carries a real DPI — then Photoshop
  // opens it at the correct physical size (pixels ÷ dpi) instead of guessing.
  function setJpegDpi(u8, dpi) {
    if (u8[0] === 0xFF && u8[1] === 0xD8 && u8[2] === 0xFF && u8[3] === 0xE0) {
      u8[13] = 1;                       // density units = dots per inch
      u8[14] = (dpi >> 8) & 0xff; u8[15] = dpi & 0xff;  // X density
      u8[16] = (dpi >> 8) & 0xff; u8[17] = dpi & 0xff;  // Y density
    }
    return u8;
  }

  function dataURLtoU8(dataURL) {
    const b = atob(dataURL.split(",")[1]);
    const u = new Uint8Array(b.length);
    for (let i = 0; i < b.length; i++) u[i] = b.charCodeAt(i);
    return u;
  }

  function trigger(url, filename) {
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    if (url.indexOf("blob:") === 0) setTimeout(() => URL.revokeObjectURL(url), 4000);
  }

  // Assemble a minimal single-page PDF with the JPEG filling the page.
  function pdfFromJpeg(jpegU8, pxW, pxH, pageWpt, pageHpt) {
    const enc = (s) => new TextEncoder().encode(s);
    const parts = []; let len = 0; const off = [];
    const push = (d) => { const u = typeof d === "string" ? enc(d) : d; parts.push(u); len += u.length; };
    const obj = (n, body) => { off[n] = len; push(n + " 0 obj\n" + body + "\nendobj\n"); };

    push("%PDF-1.4\n");
    push(new Uint8Array([37, 226, 227, 207, 211, 10])); // binary marker comment
    obj(1, "<< /Type /Catalog /Pages 2 0 R >>");
    obj(2, "<< /Type /Pages /Kids [3 0 R] /Count 1 >>");
    obj(3, "<< /Type /Page /Parent 2 0 R /MediaBox [0 0 " + pageWpt.toFixed(2) + " " + pageHpt.toFixed(2) +
      "] /Resources << /XObject << /Im0 5 0 R >> >> /Contents 4 0 R >>");
    const content = "q " + pageWpt.toFixed(2) + " 0 0 " + pageHpt.toFixed(2) + " 0 0 cm /Im0 Do Q";
    obj(4, "<< /Length " + content.length + " >>\nstream\n" + content + "\nendstream");
    // object 5: the image (binary stream)
    off[5] = len;
    push("5 0 obj\n<< /Type /XObject /Subtype /Image /Width " + pxW + " /Height " + pxH +
      " /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length " + jpegU8.length + " >>\nstream\n");
    push(jpegU8);
    push("\nendstream\nendobj\n");

    const xrefAt = len;
    let xref = "xref\n0 6\n0000000000 65535 f \n";
    for (let i = 1; i <= 5; i++) xref += String(off[i]).padStart(10, "0") + " 00000 n \n";
    push(xref);
    push("trailer\n<< /Size 6 /Root 1 0 R >>\nstartxref\n" + xrefAt + "\n%%EOF");
    return new Blob(parts, { type: "application/pdf" });
  }

  window.StudioExport = {
    // Build-only (returns a {url, filename}) so the app can present a DIRECT
    // click-to-save link — reliable across browsers, which block downloads that
    // fire automatically after an async render.
    async makeJpeg(dataUrl, filename, dpi) {
      const { dataUrl: dj } = await ensureJpeg(dataUrl, 0.95);
      let u8 = dataURLtoU8(dj);
      if (dpi) u8 = setJpegDpi(u8, dpi);
      return { url: URL.createObjectURL(new Blob([u8], { type: "image/jpeg" })), filename: filename + ".jpg" };
    },
    async makePdf(dataUrl, paperCm, landscape, filename) {
      const { dataUrl: dj, w, h } = await ensureJpeg(dataUrl, 0.95);
      const u8 = dataURLtoU8(dj);
      let cw = paperCm[0], ch = paperCm[1];
      if (landscape) { const t = cw; cw = ch; ch = t; }
      const blob = pdfFromJpeg(u8, w, h, cw * PT_PER_CM, ch * PT_PER_CM);
      return { url: URL.createObjectURL(blob), filename: filename + ".pdf" };
    },
    // Auto-download variants (kept for convenience / direct-gesture calls).
    async jpeg(dataUrl, filename, dpi) {
      const { url, filename: fn } = await this.makeJpeg(dataUrl, filename, dpi);
      trigger(url, fn);
    },
    async pdf(dataUrl, paperCm, landscape, filename) {
      const { url, filename: fn } = await this.makePdf(dataUrl, paperCm, landscape, filename);
      trigger(url, fn);
    },
  };
})();
